import { useEffect, useRef, useState } from "react";
import { Col, Nav, NavLink, Row, Spinner } from "reactstrap";
import useBestPosts from "../query/hooks/useBestPosts";
import useTrackedBestPosts from "../query/hooks/useTrackedBestPosts";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import Filter from "./Filter";
import PostCards from "./PostCards";
import { connect } from "react-redux";

const BestPostsCards = ({params, selectedCurrency, tenant, excludeOwned = false}) => {
  const trackedAccountsEnabled = !!tenant?.features?.['tracked-accounts'];
  const tabs = [
    { label: 'Eksponeringer', value: 'impression', order: false, },
    { label: 'Engasjement', value: 'engagement', order: false },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [options, setOptions] = useState([]);
  const selectedFacebookIds = options.filter(option => option.selected && option.value.startsWith('fb-')).map(option => option.value.replace('fb-', ''));
  const selectedInstagramIds = options.filter(option => option.selected && option.value.startsWith('ig-')).map(option => option.value.replace('ig-', ''));
  const selectedEarnedMedia = options.filter(option => option.selected && option.value === 'earned').map(option => option.value);
  const trackedSelected = options.some(option => option.selected && option.value === 'tracked');

  const hasSetOptions = useRef(false);

  const {data, isLoading} = useBestPosts(activeTab, {
    ...params,
    facebookIds: (hasSetOptions.current === false || selectedFacebookIds.length) >= 1 ? selectedFacebookIds : [-1],
    instagramIds: (hasSetOptions.current === false || selectedInstagramIds.length) >= 1 ? selectedInstagramIds : [-1],
    earnedMedia: (hasSetOptions.current === false || selectedEarnedMedia),
    excludeOwned,
  });
  const trackedQuery = useTrackedBestPosts(activeTab, {
    sponsorObjectId: params?.variant === 'partner' ? params?.variantId : undefined,
    enabled: trackedAccountsEnabled && (hasSetOptions.current === false || trackedSelected),
  });
  const {t} = useTranslation();

  const earnedPosts = data?.posts ?? [];
  const trackedPosts = (trackedAccountsEnabled && (hasSetOptions.current === false || trackedSelected))
    ? (trackedQuery.data?.posts ?? [])
    : [];
  // Merge and sort by the active metric; cap at 12 just like useBestPosts.
  const posts = [...earnedPosts, ...trackedPosts].sort((a, b) => {
    const diff = activeTab.order
      ? (a[activeTab.value] || 0) - (b[activeTab.value] || 0)
      : (b[activeTab.value] || 0) - (a[activeTab.value] || 0);
    // Tiebreak on id so equal metrics keep a stable order between fetches
    return diff !== 0 ? diff : String(b.id).localeCompare(String(a.id));
  }).slice(0, 12);

  useEffect(() => {
    if (!data || hasSetOptions.current) {
      return;
    }
    const socials = data.socials || {};
    const facebookData = Array.isArray(socials.facebook) ? socials.facebook : [];
    const instagramData = Array.isArray(socials.instagram) ? socials.instagram : [];

    const facebookOptions = facebookData.map((facebook) => ({
      value: `fb-${facebook.id}`,
      name: `${facebook.name}`,
      color: '#117197',
      selected: true,
    }));

    const instagramOptions = instagramData.map((instagram) => ({
      value: `ig-${instagram.id}`,
      name: `@${instagram.username}`,
      color: '#E9B42C',
      selected: true,
    }));
    const earnedMediaOption = {
      value: 'earned',
      name: 'Earned media',
      color: '#AEB2B2',
      selected: true,
    }
    const baseOptions = [...facebookOptions, ...instagramOptions, earnedMediaOption];
    if (trackedAccountsEnabled) {
      baseOptions.push({
        value: 'tracked',
        name: 'Tracked',
        color: '#EBBC32',
        selected: true,
      });
    }
    setOptions(baseOptions);
    hasSetOptions.current = true;
  }, [data]);

  return (
    <>
      {(isLoading) && <Spinner color="primary" />}
      <Nav pills className="navtab-bg center-pills mb-4">
        <NavLink
          href="#"
          className={classNames({ active: activeTab.value === tabs[0].value })}
          onClick={() => setActiveTab(tabs[0])}
        >
          {t(tabs[0].label)}
        </NavLink>
        <NavLink
          href="#"
          className={classNames({ active: activeTab.value === tabs[1].value })}
          onClick={() => setActiveTab(tabs[1])}
        >
          {t(tabs[1].label)}
        </NavLink>
      </Nav>
      <Row className="mb-4 px-4">
        <PostCards posts={posts} selectedCurrency={selectedCurrency} />
      </Row>
      {!excludeOwned && (
        <Row>
          <Col className="d-flex justify-content-center">
            <Filter options={options} setOptions={setOptions} />
          </Col>
        </Row>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  selectedCurrency: state.currency.selectedCurrency,
  tenant: state.auth.tenant,
});

export default connect(mapStateToProps)(BestPostsCards);
